import { RE2JS } from 're2js';

/** Max length for classifier regex patterns; mirrors MAX_REGEX_PATTERN_LENGTH in detect-intent-v3. */
export const REGEX_PATTERN_MAX_LENGTH = 1024;

/** Matches test sample length in the custom-entity form; applied before match preview. */
export const REGEX_TEST_TEXT_MAX_LENGTH = 200;

export const REGEX_ANCHOR_WARNING =
  'This pattern is anchored (^, $, \\A or \\z). Anchors tie the match to the start or end of the entire prompt, so a value sitting inside a sentence will never match. Consider using a \\b word boundary instead.';

// We want to warn users who use certain boundaries as this can cause unexpected regex results (not matching when they expect matching)
export const patternUsesTextAnchors = (pattern: string): boolean =>
  /[$^]|\\[Az]/.test(pattern?.replace(/\\[^Az]|\[\^/g, '') ?? '');

const RE2_INVALID_PREFIX = 'This pattern is not supported by the detection engine (RE2)';

const describeRe2Error = (error: unknown): string => {
  const detail =
    error instanceof Error ? error.message.replace(/^error parsing regexp:\s*/i, '').trim() : '';
  return detail ? `${RE2_INVALID_PREFIX}: ${detail}.` : `${RE2_INVALID_PREFIX}.`;
};

/**
 * Detections run on RE2 (see detect-intent-v3), which rejects PCRE/JS constructs the browser's own
 * RegExp accepts (lookaround, backreferences, atomic/possessive quantifiers, …) and accepts a few
 * the browser rejects ((?P<name>), POSIX classes, \Q…\E). Compiling with re2js is the source of
 * truth, so the UI accepts exactly what the backend does.
 */
export const validateRegexPattern = (pattern: string): string | null => {
  if (!pattern?.trim()) {
    return null;
  }
  if (pattern.length > REGEX_PATTERN_MAX_LENGTH) {
    return `Regex pattern must be at most ${REGEX_PATTERN_MAX_LENGTH} characters.`;
  }
  try {
    RE2JS.compile(pattern);
    return null;
  } catch (error) {
    return describeRe2Error(error);
  }
};
